/**
 * Side-panel detail view for a single execution (one taskId).
 *
 * Composition:
 *   - Top metadata strip (status pill, started, duration, taskId).
 *   - User prompt bubble + failure cause (auxiliary frames).
 *   - **Same TurnGroup component the live stream renders.** We feed the
 *     run's persisted envelopes through ``reduceAgentRoomState`` (the
 *     reducer the room uses live) and render each ``TurnGroup`` with
 *     the same component tree — ThinkingTerminal nested under each
 *     AgentResponsePanel, BuilderStreamTerminal/BuilderLogCard for
 *     builder runs, system bubbles, etc.
 *
 *     Why this matters: the deprecated drawer used a flat
 *     "transcript above, agent responses below" layout that drifted
 *     from the live view (no Builder agent surface, thinking not
 *     scoped per-agent). One template across both surfaces prevents
 *     drift and keeps the user's mental model identical whether they
 *     watch live or review later.
 *
 * Data sources:
 *   1. `getNetworkExecution(networkId, taskId)` — REST detail (SFN history
 *      summary + DDB row), authoritative for status/prompt/architect/error.
 *   2. `fetchNetworkChannelEvents(networkId, { poolId? })` — last 200 events
 *      for the network channel; we filter to envelopes whose
 *      ``correlationId`` (preferred), ``executionId``, ``taskId``, or
 *      payload-equivalents match the row. This is a best-effort: the
 *      transcript reflects whatever was persisted by the AppSync resolver
 *      at the time, which is the same data the live "Thinking" stream
 *      consumed when the run happened.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, X } from 'lucide-react';
import { getNetworkExecution } from '@/services/networkExecutionsApi';
import { fetchNetworkChannelEvents } from '@/services/networkChannelEvents';
import { envelopeEventType } from '@/constants/agentEvents';
import { reduceAgentRoomState } from '@/utils/agentRoomReducer';
import { formatRelativeTime } from '@/utils/formatDate';
import { durationBetween } from '@/utils/formatDuration';
import { bucketStatus, statusPillClassName, EXECUTION_STATUS_BUCKETS, isLiveBucket } from './executionStatus.js';
import TurnGroup from '../turn/TurnGroup.jsx';

// ``KIND_ICONS`` / ``KIND_TONE`` / ``ThinkingRow`` were the building
// blocks of the deprecated flat-transcript layout. Replaced 2026-06-02
// by ``TurnGroup``, which carries the same vocabulary as the live room
// stream — so the detail view and the live view share a single template.

/**
 * Durable fallback for the run transcript. Renders the
 * ``executionDetail.timeline`` map (one slot per agent, keyed by
 * agent id) as an ordered list of compact agent panels. Used when
 * the channel envelopes (rich, 30-day TTL) have aged out but the
 * Tasks row's durable detail blob is still present.
 *
 * Each slot becomes one card: agent name, timestamp, free-text
 * response. The Universal Builder slot also shows file changes and
 * the PR link (when push happened) since those are the durable
 * facts about what changed in the repo.
 *
 * Order is recovered from each slot's ``ts`` (ISO-8601, sorts
 * lexicographically). Slots without ``ts`` appear last.
 */
function CompactTimeline({ detail }) {
  const slots = React.useMemo(() => {
    const tl = detail && typeof detail === 'object' ? detail.timeline : null;
    if (!tl || typeof tl !== 'object') return [];
    const arr = Object.values(tl).filter(
      (s) => s && typeof s === 'object' && (s.text || s.outcome || s.files),
    );
    arr.sort((a, b) => {
      const at = String(a?.ts || '');
      const bt = String(b?.ts || '');
      if (at === bt) return 0;
      // Empty ``ts`` strings sort last so unstamped slots don't
      // jump above timestamped ones.
      if (!at) return 1;
      if (!bt) return -1;
      return at < bt ? -1 : 1;
    });
    return arr;
  }, [detail]);

  if (slots.length === 0) return null;

  return (
    <section className="mb-4">
      <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11px] text-amber-800">
        Live transcript expired. Showing the durable per-agent summary
        recorded on the Tasks row.
      </div>
      <div className="space-y-3">
        {slots.map((s) => (
          <CompactAgentCard key={s.agent || s.ts || Math.random()} slot={s} />
        ))}
      </div>
    </section>
  );
}

function CompactAgentCard({ slot }) {
  const isBuilder = String(slot?.agent || '').toLowerCase() === 'builderagent';
  const text = String(slot?.text || '').trim();
  const files = Array.isArray(slot?.files) ? slot.files : [];
  const pr = slot && typeof slot.pr === 'object' ? slot.pr : null;
  const outcome = String(slot?.outcome || '').trim();
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
      <div className="mb-1 flex items-center gap-2 text-[11px]">
        <span className="font-medium text-slate-700">{slot?.agent || 'Agent'}</span>
        {outcome ? (
          <span
            className={`rounded px-1.5 py-px text-[10px] font-medium uppercase tracking-wide ${
              outcome === 'succeeded'
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-rose-100 text-rose-700'
            }`}
          >
            {outcome}
          </span>
        ) : null}
        {slot?.ts ? (
          <span className="ml-auto font-mono text-[10px] text-slate-400">{slot.ts}</span>
        ) : null}
      </div>
      {text ? (
        <div className="whitespace-pre-wrap text-sm leading-6 text-slate-800">{text}</div>
      ) : null}
      {isBuilder && files.length > 0 ? (
        <div className="mt-2 text-[11px] text-slate-500">
          <span className="font-semibold uppercase tracking-wide text-slate-400">
            Files
          </span>
          <div className="mt-0.5 flex flex-wrap gap-1">
            {files.slice(0, 12).map((f) => (
              <span
                key={f}
                className="rounded bg-slate-100 px-1.5 py-px font-mono text-[10px] text-slate-600"
              >
                {f}
              </span>
            ))}
            {files.length > 12 ? (
              <span className="text-[10px] text-slate-400">+{files.length - 12} more</span>
            ) : null}
          </div>
        </div>
      ) : null}
      {pr && (pr.url || pr.branch) ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
          {pr.url ? (
            <a
              href={pr.url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded bg-blue-50 px-2 py-0.5 font-medium text-blue-700 hover:bg-blue-100"
            >
              PR
            </a>
          ) : null}
          {pr.branch ? (
            <span className="font-mono text-[10px] text-slate-500">{pr.branch}</span>
          ) : null}
          {typeof pr.filesChanged === 'number' && pr.filesChanged > 0 ? (
            <span className="text-[10px] text-slate-400">
              {pr.filesChanged} file{pr.filesChanged === 1 ? '' : 's'} changed
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Our ``taskId`` (Tasks row) is the Step Functions *execution name*. Many
 * persisted envelopes set that value on ``executionId`` (and
 * ``correlationId``) but **not** on ``taskId`` — e.g. ``AGENT_FINAL_RESPONSE``,
 * ``UI_STATE``, ``EXECUTION_*``. Matching only ``env.taskId`` therefore
 * dropped the whole run from the detail drawer.
 */
function normalizeExecutionName(s) {
  if (s == null || typeof s !== 'string') return '';
  const t = s.trim();
  if (!t) return '';
  if (t.startsWith('arn:') && t.includes(':execution:')) {
    const last = t.split(':').pop();
    return (last && last.trim()) || t;
  }
  return t;
}

function isMatchingEnvelope(env, { taskId, executionId, correlationId }) {
  if (!env || typeof env !== 'object') return false;
  const p = env.payload && typeof env.payload === 'object' ? env.payload : {};
  const wantTask = taskId ? normalizeExecutionName(taskId) : '';
  const wantExec = executionId ? normalizeExecutionName(executionId) : '';
  const wantCorr = correlationId ? String(correlationId).trim() : '';
  const envExec = normalizeExecutionName(env.executionId);
  const pExec = normalizeExecutionName(p.executionId);
  const envTask = typeof env.taskId === 'string' ? env.taskId : '';
  const pTask = typeof p.taskId === 'string' ? p.taskId : '';
  if (wantCorr) {
    if (env.correlationId === wantCorr || p.correlationId === wantCorr) return true;
  }
  if (wantTask) {
    if (envTask === wantTask || pTask === wantTask) return true;
    if (envExec && envExec === wantTask) return true;
    if (pExec && pExec === wantTask) return true;
    if (env.correlationId === wantTask || p.correlationId === wantTask) return true;
  }
  if (wantExec) {
    if (envExec && envExec === wantExec) return true;
    if (pExec && pExec === wantExec) return true;
    if (env.executionId === executionId || p.executionId === executionId) return true;
  }
  return false;
}

function StatusBadge({ raw }) {
  const bucket = bucketStatus(raw);
  const meta = EXECUTION_STATUS_BUCKETS[bucket];
  return (
    <span className={statusPillClassName(bucket)}>
      {isLiveBucket(bucket) ? (
        <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden />
      ) : null}
      {meta.label}
    </span>
  );
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string|null} props.taskId
 * @param {string} props.networkId
 * @param {string} [props.poolId]    Forwarded so channel-events fetch is
 *                                   scoped (the backend supports it).
 * @param {() => void} props.onClose
 */
export default function ExecutionDetailDrawer({ open, networkId, poolId, taskId, onClose }) {
  const [detail, setDetail] = useState(null);
  // ``matchedEnvelopes`` holds the run-scoped persisted events. We
  // derive ``turnGroups`` from it via the same reducer the live room
  // uses, so the rendering pipeline below is identical to the live
  // ``AgenticNetworkRoom`` view (Builder agent surface, per-agent
  // thinking, system bubbles, etc.). One template across both views.
  const [matchedEnvelopes, setMatchedEnvelopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copyHint, setCopyHint] = useState('');
  /** Optional end time from channel ``EXECUTION_COMPLETED`` when the Tasks row
   * has not been patched yet (duration would otherwise be ``—``). */
  const [endedAtFromEvents, setEndedAtFromEvents] = useState(null);
  const inflight = useRef(0);

  // Reset state every time the selected task changes — otherwise the user
  // sees a flash of the previous run's transcript while the new one loads.
  useEffect(() => {
    if (!taskId) return;
    setDetail(null);
    setMatchedEnvelopes([]);
    setError(null);
  }, [taskId]);

  useEffect(() => {
    if (!open || !taskId || !networkId) return undefined;
    const seq = inflight.current + 1;
    inflight.current = seq;
    setLoading(true);
    const abort = new AbortController();
    (async () => {
      try {
        const detailReq = getNetworkExecution(networkId, taskId, { signal: abort.signal });
        const eventsReq = fetchNetworkChannelEvents(networkId, { limit: 200, poolId }).catch(() => ({ events: [] }));
        const [det, eventsResp] = await Promise.all([detailReq, eventsReq]);
        if (inflight.current !== seq) return;
        setDetail(det || null);

        // Best-effort transcript reconstruction from persisted envelopes.
        // We don't have the original correlationId in the REST detail today
        // (backend doesn't echo it) — match on taskId/executionId.
        const matchKey = {
          taskId,
          executionId: det && typeof det.executionId === 'string' ? det.executionId : undefined,
          correlationId: det && typeof det.correlationId === 'string' ? det.correlationId : undefined,
        };
        const events = (eventsResp && Array.isArray(eventsResp.events)) ? eventsResp.events : [];
        const matched = events.filter((env) => isMatchingEnvelope(env, matchKey));
        // Channel-events GET returns newest-first; the parser expects
        // chronological order so the row ids and any state derivation
        // remain stable.
        matched.reverse();

        let bestStopMs = 0;
        let bestStopIso = null;
        for (const env of matched) {
          if (envelopeEventType(env) !== 'EXECUTION_COMPLETED') continue;
          const p = env.payload && typeof env.payload === 'object' ? env.payload : {};
          const sd = p.stopDate;
          let ms = 0;
          let iso = null;
          if (typeof sd === 'number' && Number.isFinite(sd)) {
            ms = sd;
            iso = new Date(sd).toISOString();
          } else if (typeof sd === 'string' && sd.trim()) {
            const d = Date.parse(sd);
            if (Number.isFinite(d)) {
              ms = d;
              iso = new Date(d).toISOString();
            } else {
              iso = sd.trim();
            }
          }
          if (ms >= bestStopMs) {
            bestStopMs = ms;
            bestStopIso = iso;
          } else if (!ms && iso && !bestStopIso) {
            bestStopIso = iso;
          }
        }
        setEndedAtFromEvents(bestStopIso);
        // Hand the matched envelopes to the renderer; ``reduceAgentRoomState``
        // turns them into the same TurnGroup shape the live view consumes.
        setMatchedEnvelopes(matched);
      } catch (e) {
        if (inflight.current !== seq) return;
        if (e?.name === 'AbortError') return;
        setError(e.message || 'Failed to load execution');
      } finally {
        if (inflight.current === seq) setLoading(false);
      }
    })();
    return () => abort.abort();
  }, [open, networkId, poolId, taskId]);

  const headerSummary = useMemo(() => {
    const status = detail?.status;
    const created = detail?.createdAt;
    const ended = detail?.endedAt || detail?.completedAt || detail?.updatedAt || endedAtFromEvents;
    return {
      status,
      started: created,
      duration: durationBetween(created, ended),
    };
  }, [detail, endedAtFromEvents]);

  // Derive turn groups from the matched envelopes. Same reducer the
  // live room uses, so a turn rendered here looks identical to the
  // turn the user saw streaming live. We force any open turn to
  // ``completed`` because by drawer-open time the run has terminated;
  // leaving ``status: 'live'`` would render misleading "still running"
  // affordances inside the TurnGroup (animated activeAgent pulse,
  // ``Awaiting approval`` band, etc.).
  const turnGroups = useMemo(() => {
    if (!Array.isArray(matchedEnvelopes) || matchedEnvelopes.length === 0) return [];
    const state = reduceAgentRoomState(matchedEnvelopes);
    const tgs = Array.isArray(state.turnGroups) ? state.turnGroups : [];
    return tgs.map((t) => (t.status === 'live' ? { ...t, status: 'completed' } : t));
  }, [matchedEnvelopes]);

  function copyTaskId() {
    if (!taskId || !navigator.clipboard) return;
    navigator.clipboard.writeText(taskId).then(
      () => {
        setCopyHint('Copied');
        window.setTimeout(() => setCopyHint(''), 1200);
      },
      () => {
        setCopyHint('Copy failed');
        window.setTimeout(() => setCopyHint(''), 1200);
      },
    );
  }

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="exec-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/30"
          />
          <motion.aside
            key="exec-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-2xl flex-col bg-white shadow-xl"
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-sm font-semibold text-slate-900">Execution detail</h2>
                  <StatusBadge raw={headerSummary.status} />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                  {headerSummary.started ? (
                    <span>Started {formatRelativeTime(headerSummary.started)}</span>
                  ) : null}
                  <span>Duration {headerSummary.duration}</span>
                  {taskId ? (
                    <button
                      type="button"
                      onClick={copyTaskId}
                      className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-600 hover:bg-slate-50"
                      aria-label="Copy task id"
                      title={taskId}
                    >
                      <span className="truncate max-w-[180px]">{taskId}</span>
                      {copyHint ? <span className="text-emerald-600">{copyHint}</span> : null}
                    </button>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                aria-label="Close execution detail"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {loading && !detail ? (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Loading execution…
                </div>
              ) : null}

              {error ? (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {error}
                </div>
              ) : null}

              {detail?.prompt ? (
                <section className="mb-4">
                  <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    Prompt
                  </h3>
                  <div className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm text-slate-800">
                    {detail.prompt}
                  </div>
                </section>
              ) : null}

              {detail?.error ? (
                <section className="mb-4">
                  <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-rose-600">
                    Failure cause
                  </h3>
                  <div className="whitespace-pre-wrap rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-mono text-rose-800">
                    {String(detail.error)}
                  </div>
                </section>
              ) : null}

              {detail?.architectExplanation && bucketStatus(detail?.status) === 'awaiting' ? (
                <section className="mb-4">
                  <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    Awaiting approval — design
                  </h3>
                  <pre className="whitespace-pre-wrap rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs font-mono text-amber-900">
                    {String(detail.architectExplanation)}
                  </pre>
                </section>
              ) : null}

              {/*
               * Render priority for the run transcript:
               *   1. Channel envelopes are present  → live-stream template
               *      (TurnGroup). Same component the room renders, so the
               *      drawer and live view never drift.
               *   2. Envelopes empty + executionDetail blob present →
               *      durable CompactTimeline. Channel envelopes age out
               *      at 30 days; the executionDetail blob is co-located
               *      on the Tasks row (forever) so old runs still show
               *      a meaningful summary.
               *   3. Neither → "no transcript" placeholder.
               */}
              {turnGroups.length > 0 ? (
                <section className="mb-4 space-y-4">
                  {turnGroups.map((turn) => (
                    <TurnGroup key={turn.id} turn={turn} networkId={networkId} />
                  ))}
                </section>
              ) : detail?.executionDetail ? (
                <CompactTimeline detail={detail.executionDetail} />
              ) : (
                <section className="mb-4">
                  <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    Run transcript
                  </h3>
                  <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                    No transcript persisted for this run.
                  </div>
                </section>
              )}
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}
